import { useMemo } from 'react';
import {
  userHasPermissionForProject,
  userHasPermissionToSetProjectFilters,
  userHasPermissionToSetProjectPolicy,
} from '../../../helpers/permissions';
import { Action } from '../../../../typings/permissions';
import { window } from '../../../globals';
import { PolicyType } from '../../../../typings/enums';

export const useCanUserEditProject = (locator: string) =>
  useMemo(() => userHasPermissionForProject(Action.Edit, locator),
    // We need to include the permissions because that is what hasPermission is looking at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locator, window.user?.permissions]);

export const useCanUserDeleteProject = (locator: string) =>
  useMemo(() => userHasPermissionForProject(Action.Delete, locator),
    // We need to include the permissions because that is what hasPermission is looking at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locator, window.user?.permissions]);

export const useCanUserMakeProjectPublic = (locator: string) =>
  useMemo(() => userHasPermissionForProject(Action.MakePublic, locator),
    // We need to include the permissions because that is what hasPermission is looking at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locator, window.user?.permissions]);

export const useCanUserSetLicensingPolicy = (locator: string) =>
  useMemo(() => userHasPermissionToSetProjectPolicy(locator, PolicyType.LICENSING),
    // We need to include the permissions because that is what hasPermission is looking at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locator, window.user?.permissions]);

export const useCanUserSetSecurityPolicy = (locator: string) =>
  useMemo(() => userHasPermissionToSetProjectPolicy(locator, PolicyType.SECURITY),
    // We need to include the permissions because that is what hasPermission is looking at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locator, window.user?.permissions]);

export const useCanUserSetQualityPolicy = (locator: string) =>
  useMemo(() => userHasPermissionToSetProjectPolicy(locator, PolicyType.QUALITY),
    // We need to include the permissions because that is what hasPermission is looking at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locator, window.user?.permissions]);

export const useCanUserSetSBOMPolicy = (locator: string) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We need to include the permissions because that is what hasPermission is looking at.
  useMemo(() => userHasPermissionToSetProjectPolicy(locator, PolicyType.SBOM), [locator, window.user?.permissions]);

export const useCanUserSetSomeIssuePolicy = (locator: string) => {
  const canSetLicensingPolicy = useCanUserSetLicensingPolicy(locator);
  const canSetSecurityPolicy = useCanUserSetSecurityPolicy(locator);
  const canSetQualityPolicy = useCanUserSetQualityPolicy(locator);
  return canSetLicensingPolicy || canSetSecurityPolicy || canSetQualityPolicy;
};

export const useCanUserSetLicensingIssueFilter = (locator: string) =>
  useMemo(() => userHasPermissionToSetProjectFilters(locator, PolicyType.LICENSING),
    // We need to include the permissions because that is what hasPermission is looking at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locator, window.user?.permissions]);

export const useCanUserSetSecurityIssueFilter = (locator: string) =>
  useMemo(() => userHasPermissionToSetProjectFilters(locator, PolicyType.SECURITY),
    // We need to include the permissions because that is what hasPermission is looking at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locator, window.user?.permissions]);

export const useCanUserSetQualityIssueFilter = (locator: string) =>
  useMemo(() => userHasPermissionToSetProjectFilters(locator, PolicyType.QUALITY),
    // We need to include the permissions because that is what hasPermission is looking at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locator, window.user?.permissions]);

export const useCanUserSetSomeIssueFilter = (locator: string) => {
  const canSetLicensingFilter = useCanUserSetLicensingIssueFilter(locator);
  const canSetSecurityFilter = useCanUserSetSecurityIssueFilter(locator);
  const canSetQualityFilter = useCanUserSetQualityIssueFilter(locator);
  return canSetLicensingFilter || canSetSecurityFilter || canSetQualityFilter;
};
